import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useHistory } from 'react-router-dom';
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@material-ui/core';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AlertDialog from '../../common/AlertDialog';
import EnhancedTable from '../../components/enhancedTable/enhancedTable';
import {
  EndpointSubtype,
  EndpointSubtypeNames,
  EndpointType,
  EndpointTypeNames,
  FetchScriptsParams,
  InputType,
  InputTypeNames,
  Script,
} from './types';
import { formatDate } from './helpers/common';

interface ScriptListProps {
  orgId?: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;

const ENDPOINT_TYPE_OPTIONS = Object.values(EndpointType).filter(
  (v) => typeof v === 'number',
) as EndpointType[];
const ENDPOINT_SUBTYPE_OPTIONS = Object.values(EndpointSubtype).filter(
  (v) => typeof v === 'number',
) as EndpointSubtype[];
const INPUT_TYPE_OPTIONS = Object.values(InputType).filter(
  (v) => typeof v === 'number',
) as InputType[];

const ScriptList: React.FC<ScriptListProps> = ({ orgId: orgIdProp }) => {
  const history = useHistory();
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [endpointTypeId, setEndpointTypeId] = useState<EndpointType | ''>('');
  const [endpointSubtypeId, setEndpointSubtypeId] = useState<
    EndpointSubtype | ''
  >('');
  const [inputTypeId, setInputTypeId] = useState<InputType | ''>('');

  const storeOrgId = useStoreState(
    (state: any) => state.organization.organizationId,
  );
  const orgId = orgIdProp ?? storeOrgId;
  const isOrgContext = !!orgIdProp;
  const routeBase = isOrgContext
    ? `/organization/${encodeURIComponent(orgId)}/transformations`
    : '/scripts';

  const scriptList: Script[] = useStoreState(
    (state: any) => state.script.scriptList,
  );
  const totalPages: number = useStoreState(
    (state: any) => state.script.scriptListTotalPages,
  );

  const scriptAction = useStoreActions((actions: any) => actions.script);
  const commonAction = useStoreActions((actions: any) => actions.common);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        sortType: 'caseInsensitive',
      },
      {
        Header: 'Subtypes',
        Cell: ({ row }: any) =>
          ((row.original.endpointSubtypeIds ?? []) as EndpointSubtype[])
            .map((id) => EndpointSubtypeNames[id])
            .join(', ') || '-',
      },
      {
        Header: 'Last Modified',
        Cell: ({ row }: any) => formatDate(row.original.lastModifiedDate),
      },
      {
        Header: ' ',
        Cell: ({ row }: any) => (
          <div style={{ display: 'flex' }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => history.push(`${routeBase}/${row.original.id}`)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setDeleteItem(row.original);
                  setDeleteAlert(true);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [history, routeBase],
  );

  const buildParams = useCallback(
    (page: number, size: number): FetchScriptsParams => ({
      pageNumber: page,
      pageSize: size,
      ...(search && { search }),
      ...(endpointTypeId !== '' && { endpointTypeId }),
      ...(endpointSubtypeId !== '' && { endpointSubtypeId }),
      ...(inputTypeId !== '' && { inputTypeId }),
    }),
    [search, endpointTypeId, endpointSubtypeId, inputTypeId],
  );

  const fetchList = useCallback(
    (page: number, size: number) => {
      scriptAction.getScriptList({ orgId, params: buildParams(page, size) });
    },
    [orgId, scriptAction, buildParams],
  );

  const handleFilterChange = useCallback(
    (
      newEndpointTypeId: EndpointType | '',
      newEndpointSubtypeId: EndpointSubtype | '',
      newInputTypeId: InputType | '',
    ) => {
      setCurrentPage(DEFAULT_PAGE);
      scriptAction.getScriptList({
        orgId,
        params: {
          pageNumber: DEFAULT_PAGE,
          pageSize,
          ...(search && { search }),
          ...(newEndpointTypeId !== '' && {
            endpointTypeId: newEndpointTypeId,
          }),
          ...(newEndpointSubtypeId !== '' && {
            endpointSubtypeId: newEndpointSubtypeId,
          }),
          ...(newInputTypeId !== '' && { inputTypeId: newInputTypeId }),
        },
      });
    },
    [orgId, scriptAction, search, pageSize],
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setCurrentPage(newPage);
    fetchList(newPage, pageSize);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setCurrentPage(DEFAULT_PAGE);
    fetchList(DEFAULT_PAGE, newSize);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(DEFAULT_PAGE);
      fetchList(DEFAULT_PAGE, pageSize);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, search]);

  const deleteAlertDialogOpts = useMemo(
    () => ({
      title: `Delete - ${deleteItem?.name || ''}`,
      description: `Do you want to remove script "${deleteItem?.name || ''}"?`,
      btnOkText: 'Yes',
      btnCancelText: 'Cancel',
      onClose: (confirmed: boolean) => {
        if (confirmed && deleteItem?.id) {
          scriptAction.deleteScript({
            orgId,
            id: deleteItem.id,
            params: buildParams(currentPage, pageSize),
            commonAction,
          });
        }
        setDeleteAlert(false);
        setDeleteItem(null);
      },
    }),
    [
      buildParams,
      commonAction,
      currentPage,
      deleteItem?.id,
      deleteItem?.name,
      orgId,
      pageSize,
      scriptAction,
    ],
  );

  return (
    <Grid item md={12}>
      <EnhancedTable
        columns={columns}
        data={scriptList}
        skipPageReset={false}
        paginationMode="server"
        pages={totalPages}
        limit={pageSize}
        currentPage={currentPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        paginationDefauldSize={undefined}
        rowSelection={undefined}
        sortBy={undefined}
        paging={undefined}
        loader={undefined}
        toolbar={{
          show: true,
          title: 'Transformations',
          enableSearch: false,
          actionButtonGroup: () => (
            <Box
              display="flex"
              flex={1}
              gridGap={'8px'}
              justifyContent="center"
              alignItems="center"
            >
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 180 }}
              />
              <FormControl
                size="small"
                variant="outlined"
                style={{ width: 110 }}
              >
                <InputLabel>Type</InputLabel>
                <Select
                  value={endpointTypeId}
                  label="Type"
                  onChange={(e) => {
                    const val = e.target.value as EndpointType | '';
                    setEndpointTypeId(val);
                    handleFilterChange(val, endpointSubtypeId, inputTypeId);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {ENDPOINT_TYPE_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {EndpointTypeNames[v]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                variant="outlined"
                style={{ width: 180 }}
              >
                <InputLabel>Subtype</InputLabel>
                <Select
                  value={endpointSubtypeId}
                  label="Subtype"
                  onChange={(e) => {
                    const val = e.target.value as EndpointSubtype | '';
                    setEndpointSubtypeId(val);
                    handleFilterChange(endpointTypeId, val, inputTypeId);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {ENDPOINT_SUBTYPE_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {EndpointSubtypeNames[v]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                variant="outlined"
                style={{ width: 100 }}
              >
                <InputLabel>Input</InputLabel>
                <Select
                  value={inputTypeId}
                  label="Input"
                  onChange={(e) => {
                    const val = e.target.value as InputType | '';
                    setInputTypeId(val);
                    handleFilterChange(endpointTypeId, endpointSubtypeId, val);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {INPUT_TYPE_OPTIONS.map((v) => (
                    <MenuItem key={v} value={v}>
                      {InputTypeNames[v]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Add Transformation">
                <IconButton
                  color="primary"
                  aria-label="Add"
                  onClick={() => history.push(`${routeBase}/new`)}
                >
                  <AddBoxIcon />
                </IconButton>
              </Tooltip>
              {/* TODO: in progress */}
              {/* <Tooltip title="Testing">
                <IconButton
                  color="primary"
                  aria-label="Testing"
                  onClick={() => history.push(`${routeBase}/testing`)}
                >
                  <BugReportIcon />
                </IconButton>
              </Tooltip> */}
            </Box>
          ),
        }}
      />
      <AlertDialog opts={deleteAlertDialogOpts} open={deleteAlert} />
    </Grid>
  );
};

export default ScriptList;
